/**
 * Charge Heads Mode-Wise config aligned with "Charge Heads Mode Wise" sheet.
 * Level 1 = Mode (Service Type), Level 2 = Leg (Scope), Level 3 = Charge Head.
 * Scope options and charge codes follow Service Type; some modes (see MODES_CHARGE_DROPDOWN_SHOWS_ALL_LEGS) list all charge heads regardless of Scope.
 */

// Scope (Level 2 → Leg) options per Service Type (Level 1 → Mode)
export const SCOPE_OPTIONS_BY_MODE = {
  'ocean-fcl': [
    'Origin Inland (FCL)',
    'Origin Customs (FCL)',
    'Origin Port Terminal (FCL)',
    'Ocean Freight (FCL)',
    'Destination Port (FCL)',
    'Destination Customs (FCL)',
    'Destination Inland (FCL)'
  ],
  'ocean-lcl': [
    'Origin Inland (LCL)',
    'Origin CFS / Warehouse (LCL)',
    'Origin Customs (LCL)',
    'Origin Terminal (LCL)',
    'Ocean Freight (LCL)',
    'Destination CFS (LCL)',
    'Destination Customs (LCL)',
    'Destination Inland (LCL)',
    'Special Cargo / Value Added'
  ],
  air: [
    'Origin Pick-up & Handling',
    'Origin Clearance',
    'Origin Documentation',
    'Airline / Terminal Charges',
    'Air Freight Rate',
    'Destination Air Charges',
    'Import Clearance',
    'Destination Inland'
  ],
  transport: [
    'Origin Pick-up',
    'Origin Handling',
    'Line Haul',
    'Destination Handling',
    'Delivery'
  ],
  warehousing: [
    'Handling',
    'Storage',
    'Compliance',
    'Transport Interface',
    'Value Added',
    'Warehouse Documentation & System'
  ],
  breakbulk: [
    'Origin Inland Movement',
    'Origin Handling / Terminal',
    'Origin Documentation & Customs',
    'Origin Port & Terminal Charges',
    'Ocean Freight Components',
    'Transshipment / Intermediate Port',
    'Destination Port Charges',
    'Import Clearance',
    'Destination Inland Transport'
  ]
};

// Charge heads per Mode and Leg: { [modeId]: { [scopeLabel]: [{ code, name }, ...] } }
// Codes are used in line items; names are shown in dropdown.
const buildChargeHeadsByModeLeg = () => {
  const FCL = {
    'Origin Inland (FCL)': [
      { code: 'FCL_PU_TRAILER', name: 'Factory Pick Up Trailer Charges' },
      { code: 'FCL_EMP_POS', name: 'Empty Container Positioning' },
      { code: 'FCL_TRAILER_DET', name: 'Trailer Detention' },
      { code: 'FCL_TRUCK_WAIT', name: 'Truck Waiting Charges' },
      { code: 'FCL_CHASSIS', name: 'Chassis Charges' },
      { code: 'FCL_TOLL_FUEL', name: 'Toll / Fuel Surcharge' },
      { code: 'FCL_FACTORY_STUFF', name: 'Factory Stuffing Charges' },
      { code: 'FCL_LABOUR_LOAD', name: 'Labour Loading Charges' },
      { code: 'FCL_WEIGHMENT', name: 'Weighment Charges' },
      { code: 'FCL_VGM', name: 'VGM Filing Charges' }
    ],
    'Origin Customs (FCL)': [
      { code: 'CUSTOMS', name: 'Export Customs Clearance' },
      { code: 'FCL_SHIP_BILL', name: 'Shipping Bill Filing' },
      { code: 'FCL_CHA', name: 'CHA Handling' },
      { code: 'FCL_EXAM', name: 'Examination Charges' },
      { code: 'SEAL', name: 'Seal Charges' },
      { code: 'FCL_CARTING', name: 'Carting (if required at port CFS)' }
    ],
    'Origin Port Terminal (FCL)': [
      { code: 'OTHC', name: 'OTHC (Per Container)' },
      { code: 'DOC', name: 'Port Documentation Fee' },
      { code: 'FCL_ISPS', name: 'ISPS Charges' },
      { code: 'FCL_TERM_SEC', name: 'Terminal Security Charges' },
      { code: 'FCL_PORT_CONG', name: 'Port Congestion Surcharge' },
      { code: 'FCL_SCAN', name: 'Scanning Charges' },
      { code: 'FCL_LIFT_ON', name: 'Lift On Charges' },
      { code: 'FCL_REEFER_PLUG', name: 'Reefer Plug-in Charges' }
    ],
    'Ocean Freight (FCL)': [
      { code: 'OFR', name: 'Ocean Freight Per Container' },
      { code: 'FCL_BAF_LSS', name: 'BAF / LSS' },
      { code: 'FCL_PSS_GRI', name: 'PSS / GRI' },
      { code: 'FCL_WAR', name: 'War Risk Surcharge' },
      { code: 'FCL_EQUIP_IMB', name: 'Equipment Imbalance Surcharge' },
      { code: 'FCL_SPACE_GUAR', name: 'Space Guarantee Charges' },
      { code: 'DOC', name: 'Carrier Documentation Fee' },
      { code: 'FCL_BOOKING', name: 'Booking Fee' },
      { code: 'FCL_FREIGHT_LIC', name: 'Freight License (China trade)' }
    ],
    'Destination Port (FCL)': [
      { code: 'DTHC', name: 'DTHC (Per Container)' },
      { code: 'FCL_AGENCY', name: 'Carrier Agency Fee' },
      { code: 'FCL_DO', name: 'Delivery Order Charges' },
      { code: 'BL', name: 'BL Fee / Telex Fee' },
      { code: 'FCL_ISPS_DEST', name: 'ISPS Destination' },
      { code: 'FCL_POD_CONG', name: 'Port Congestion' }
    ],
    'Destination Customs (FCL)': [
      { code: 'CUSTOMS', name: 'Import Customs Clearance' },
      { code: 'FCL_BOE', name: 'Bill of Entry Filing' },
      { code: 'FCL_CUSTOMS_EXAM', name: 'Customs Examination' },
      { code: 'DUTY', name: 'Duty Handling Charges' },
      { code: 'FCL_LICENCE', name: 'License Handling' }
    ],
    'Destination Inland (FCL)': [
      { code: 'TRANSPORT', name: 'Container Transportation' },
      { code: 'FCL_TRAILER_DET_D', name: 'Trailer Detention' },
      { code: 'FCL_GROUND_RENT', name: 'Ground Rent' },
      { code: 'FCL_LIFT_OFF', name: 'Container Lift Off' },
      { code: 'FCL_EMP_RETURN', name: 'Empty Container Return Charges' },
      { code: 'FCL_DEM_DET', name: 'Demurrage / Detention' },
      { code: 'DELIVERY', name: 'Door Delivery Trucking' }
    ]
  };

  const LCL = {
    'Origin Inland (LCL)': [
      { code: 'LCL_PICKUP', name: 'Pick Up Charges' },
      { code: 'LCL_CARTAGE', name: 'Local Cartage' },
      { code: 'LCL_LABOUR', name: 'Labour Loading' },
      { code: 'LCL_PALLET', name: 'Palletisation' },
      { code: 'LCL_PACKING', name: 'Packing / Repacking' },
      { code: 'LCL_SHRINK', name: 'Shrink Wrapping' },
      { code: 'LCL_MARKING', name: 'Marking & Labelling' }
    ],
    'Origin CFS / Warehouse (LCL)': [
      { code: 'CFS', name: 'CFS Receiving Charges' },
      { code: 'LCL_DOCK', name: 'Dock Handling Charges' },
      { code: 'WAREHOUSE', name: 'CFS Storage' },
      { code: 'LCL_CONSOL', name: 'Consolidation Charges' },
      { code: 'LCL_PALLET_HAND', name: 'Pallet Handling Charges' },
      { code: 'LCL_FORKLIFT', name: 'Forklift Charges' },
      { code: 'LCL_MEASURE', name: 'Measurement / Weighment Charges' },
      { code: 'LCL_STUFFING', name: 'Stuffing Charges' },
      { code: 'DOC', name: 'Documentation at CFS' }
    ],
    'Origin Customs (LCL)': [
      { code: 'CUSTOMS', name: 'Export Clearance Charges' },
      { code: 'LCL_SHIP_BILL', name: 'Shipping Bill Filing' },
      { code: 'LCL_EXAM', name: 'Examination Handling' },
      { code: 'SEAL', name: 'Seal Charges' },
      { code: 'LCL_AGENCY', name: 'Agency Handling' }
    ],
    'Origin Terminal (LCL)': [
      { code: 'THC', name: 'LCL THC (Per W/M)' },
      { code: 'LCL_PORT_SEC', name: 'Port Security Charges' },
      { code: 'LCL_ISPS', name: 'ISPS Charges' },
      { code: 'LCL_WHARFAGE', name: 'Wharfage (Sometimes W/M basis)' },
      { code: 'WAREHOUSE', name: 'Port Storage' }
    ],
    'Ocean Freight (LCL)': [
      { code: 'OFR', name: 'Ocean Freight Per CBM / W/M' },
      { code: 'LCL_MIN', name: 'Minimum Freight (1 CBM rule)' },
      { code: 'LCL_BAF', name: 'BAF / LSS' },
      { code: 'LCL_PSS', name: 'PSS' },
      { code: 'LCL_WAR', name: 'War Risk' },
      { code: 'LCL_CONSOL_MARG', name: 'Consolidation Margin' },
      { code: 'LCL_COLOAD', name: 'Co-Loader Fees' }
    ],
    'Destination CFS (LCL)': [
      { code: 'LCL_CFS_HAND', name: 'Destination CFS Handling' },
      { code: 'LCL_DECONSOL', name: 'Deconsolidation Charges' },
      { code: 'DOC', name: 'CFS Documentation' },
      { code: 'LCL_HAND_CBM', name: 'Handling Per CBM' },
      { code: 'LCL_FREE_DAYS', name: 'CFS Storage Free Days' },
      { code: 'LCL_FORKLIFT_D', name: 'Forklift Handling' },
      { code: 'LCL_PALLET_BREAK', name: 'Pallet Breakdown' }
    ],
    'Destination Customs (LCL)': [
      { code: 'CUSTOMS', name: 'Import Clearance' },
      { code: 'LCL_BOE', name: 'Bill of Entry' },
      { code: 'LCL_EXAM_D', name: 'Examination Handling' },
      { code: 'DUTY', name: 'Duty Handling' }
    ],
    'Destination Inland (LCL)': [
      { code: 'LCL_CARTAGE_D', name: 'Local Cartage Per Shipment' },
      { code: 'DELIVERY', name: 'Last Mile Delivery' },
      { code: 'LCL_MULTI_DROP', name: 'Multi Drop Charges' },
      { code: 'LCL_DOOR_HAND', name: 'Door Delivery Handling' },
      { code: 'LCL_APPT', name: 'Warehouse Delivery Appointment' }
    ],
    'Special Cargo / Value Added': [
      { code: 'LCL_REEFER_MON', name: 'Reefer Monitoring' },
      { code: 'LCL_DG', name: 'DG Handling Charges' },
      { code: 'LCL_IMO', name: 'IMO Surcharge' },
      { code: 'LCL_TEMP', name: 'Temperature Control Charges' },
      { code: 'INSURANCE', name: 'Insurance' },
      { code: 'LCL_SURVEY', name: 'Survey Charges' },
      { code: 'LCL_TRACKING', name: 'Cargo Tracking Fee' },
      { code: 'LCL_GREEN', name: 'Green Tax / Environmental Fee' }
    ]
  };

  const AIR = {
    'Origin Pick-up & Handling': [
      { code: 'PICKUP', name: 'Pick Up Charges' },
      { code: 'AIR_WAIT', name: 'Waiting / Loading Charges' },
      { code: 'AIR_PACK', name: 'Packing Charges' },
      { code: 'AIR_PALLET', name: 'Palletisation' },
      { code: 'AIR_XRAY', name: 'X-Ray Screening' },
      { code: 'AIR_SEC', name: 'Security Handling' },
      { code: 'AIR_WEIGH', name: 'Weighment' },
      { code: 'AIR_PASS', name: 'Airport Entry Pass' }
    ],
    'Origin Clearance': [
      { code: 'CUSTOMS', name: 'Export Customs Clearance' },
      { code: 'AIR_SHIP_BILL', name: 'Shipping Bill Filing' },
      { code: 'AIR_CHA', name: 'CHA Charges' },
      { code: 'AIR_EXAM', name: 'Examination Charges' },
      { code: 'AIR_CUSTOMS_FEE', name: 'Customs Processing Fees' }
    ],
    'Origin Documentation': [
      { code: 'AIR_AWB', name: 'AWB Fee' },
      { code: 'DOC', name: 'Documentation Charges' },
      { code: 'AIR_LC', name: 'LC Handling Charges' },
      { code: 'AIR_COO', name: 'COO Handling' },
      { code: 'AIR_DG_DOC', name: 'DG Documentation' },
      { code: 'AIR_SHIPPER', name: 'Shipper Declaration Fee' },
      { code: 'AIR_AMS', name: 'AMS Filing' },
      { code: 'AIR_ENS', name: 'ENS Filing' }
    ],
    'Airline / Terminal Charges': [
      { code: 'AIR_TERM_ORIG', name: 'Origin Terminal Handling' },
      { code: 'AIR_AIRLINE', name: 'Airline Handling Fee' },
      { code: 'AIR_APT_HAND', name: 'Airport Handling Charges' },
      { code: 'AIR_BUILDUP', name: 'Build-Up Charges' },
      { code: 'AIR_ULD', name: 'ULD Handling' },
      { code: 'AIR_STORAGE', name: 'Storage Charges' },
      { code: 'AIR_APT_SEC', name: 'Airport Security Charges' }
    ],
    'Air Freight Rate': [
      { code: 'AFR', name: 'Basic Air Freight' },
      { code: 'AIR_FSC', name: 'FSC (Fuel Surcharge)' },
      { code: 'AIR_SSC', name: 'SSC (Security Surcharge)' },
      { code: 'AIR_WAR', name: 'War Risk Surcharge' },
      { code: 'AIR_SCREEN', name: 'Screening Charges' },
      { code: 'AIR_CONG', name: 'Congestion Surcharge' },
      { code: 'AIR_PSS', name: 'Peak Season Surcharge' },
      { code: 'AIR_TACT', name: 'TACT Rate Adjustment' },
      { code: 'AIR_CHARTER', name: 'Charter Cost' },
      { code: 'AIR_EXPRESS', name: 'Express Handling Premium' }
    ],
    'Destination Air Charges': [
      { code: 'AIR_TERM_DEST', name: 'Destination Terminal Handling' },
      { code: 'AIR_DO', name: 'Airline DO Fee' },
      { code: 'AIR_HAND_DEST', name: 'Handling Charges' },
      { code: 'AIR_STORAGE_D', name: 'Airport Storage' },
      { code: 'AIR_SEC_D', name: 'Airport Security' }
    ],
    'Import Clearance': [
      { code: 'CUSTOMS', name: 'Import Customs Clearance' },
      { code: 'AIR_BOE', name: 'Bill of Entry Filing' },
      { code: 'DUTY', name: 'Duty Handling Charges' },
      { code: 'AIR_IMPORT_EXAM', name: 'Examination Charges' },
      { code: 'AIR_LICENSE', name: 'License Handling' }
    ],
    'Destination Inland': [
      { code: 'DELIVERY', name: 'Delivery Charges' },
      { code: 'AIR_LAST_FUEL', name: 'Fuel Surcharge' },
      { code: 'AIR_TOLL', name: 'Toll' },
      { code: 'AIR_LAST_WAIT', name: 'Waiting' },
      // Reuse existing `TRANSPORT` code (already used by older quotes in this mode) so name mapping doesn't break.
      { code: 'TRANSPORT', name: 'Warehouse Handling' },
      { code: 'AIR_DOOR_DELIVERY', name: 'Door Delivery' }
    ]
  };

  const TRANSPORT = {
    'Origin Pick-up': [
      { code: 'PICKUP', name: 'Pick Up Charges' },
      { code: 'TRAN_FIRST_MILE_TRANSPORT', name: 'First Mile Transportation' },
      { code: 'TRAN_LOCAL_CARTAGE', name: 'Local Cartage Charges' },
      { code: 'TRAN_TRUCK_PLACEMENT', name: 'Truck Placement Charges' },
      { code: 'TRAN_VEHICLE_POSITIONING', name: 'Vehicle Positioning Charges' },
      { code: 'TRAN_EMPTY_RUN', name: 'Empty Run Charges' }
    ],
    'Origin Handling': [
      { code: 'TRAN_LOADING_CHARGES', name: 'Loading Charges' },
      { code: 'TRAN_UNLOADING_CHARGES', name: 'Unloading Charges' },
      { code: 'TRAN_MANUAL_LABOUR_CHARGES', name: 'Manual Labour Charges' },
      { code: 'TRAN_FORKLIFT_HANDLING_CHARGES', name: 'Forklift Handling Charges' },
      { code: 'TRAN_CRANE_HANDLING_CHARGES', name: 'Crane Handling Charges' },
      { code: 'TRAN_PALLET_HANDLING_CHARGES', name: 'Pallet Handling Charges' },
      { code: 'TRAN_CONTAINER_STUFFING_CHARGES', name: 'Container Stuffing Charges' },
      { code: 'TRAN_CONTAINER_DESTUFFING_CHARGES', name: 'Container Destuffing Charges' },
      { code: 'TRAN_TRUCK_WAITING_CHARGES', name: 'Truck Waiting Charges' },
      { code: 'TRAN_DETENTION_PER_HOUR_CHARGES', name: 'Detention Charges (Per Hour)' },
      { code: 'TRAN_VEHICLE_STANDBY_CHARGES', name: 'Vehicle Standby Charges' },
      { code: 'TRAN_DRIVER_WAITING_CHARGES', name: 'Driver Waiting Charges' },
      { code: 'TRAN_DELAYED_LOADING_CHARGES', name: 'Delayed Loading Charges' }
    ],
    'Line Haul': [
      { code: 'TRAN_BASE_FREIGHT_KM_MILE', name: 'Base Freight (Per KM / Mile)' },
      { code: 'TRAN_FLAT_LINEHAUL_RATE', name: 'Flat Linehaul Rate' },
      { code: 'TRAN_PER_TON_FREIGHT', name: 'Per Ton Freight' },
      { code: 'TRAN_PER_CBM_FREIGHT', name: 'Per CBM Freight' },
      { code: 'TRAN_PER_CONTAINER_TRANSPORT', name: 'Per Container Transport Charges (20FT / 40FT / 40HC / 45FT)' },
      { code: 'TRAN_PER_TRUCK_FTL', name: 'Per Truck Freight (FTL)' },
      { code: 'TRAN_PER_PALLET_LTL', name: 'Per Pallet Freight (LTL)' },
      { code: 'TRAN_PER_SHIPMENT_FREIGHT', name: 'Per Shipment Freight' },
      { code: 'TRAN_MINIMUM_FREIGHT_CHARGES', name: 'Minimum Freight Charges' },
      { code: 'TRAN_ROUND_TRIP_FREIGHT', name: 'Round Trip Freight' },
      { code: 'TRAN_DEDICATED_TRUCK_CHARGES', name: 'Dedicated Truck Charges' },
      { code: 'TRAN_EXPRESS_DELIVERY_PREMIUM', name: 'Express Delivery Premium' },

      { code: 'TRAN_FUEL_SURCHARGE_FSC', name: 'Fuel Surcharge (FSC)' },
      { code: 'TRAN_DIESEL_ADJUSTMENT_FACTOR', name: 'Diesel Adjustment Factor' },
      { code: 'TRAN_FUEL_ESCALATION_SURCHARGE', name: 'Fuel Escalation Surcharge' },

      { code: 'TRAN_TRAILER_CHARGES', name: 'Trailer Charges' },
      { code: 'TRAN_CHASSIS_USAGE_CHARGES', name: 'Chassis Usage Charges' },
      { code: 'TRAN_FLATBED_TRUCK_CHARGES', name: 'Flatbed Truck Charges' },
      { code: 'TRAN_LOWBD_TRAILER_CHARGES', name: 'Lowbed Trailer Charges' },
      { code: 'TRAN_REEFER_TRUCK_CHARGES', name: 'Reefer Truck Charges' },
      { code: 'TRAN_TANKER_TRUCK_CHARGES', name: 'Tanker Truck Charges' },
      { code: 'TRAN_MULTI_AXLE_TRAILER_CHARGES', name: 'Multi Axle Trailer Charges' },
      { code: 'TRAN_HYDRAULIC_TRAILER_CHARGES', name: 'Hydraulic Trailer Charges' },
      { code: 'TRAN_EXTENDABLE_TRAILER_CHARGES', name: 'Extendable Trailer Charges' },
      { code: 'TRAN_SPECIAL_EQUIPMENT_SURCHARGE', name: 'Special Equipment Surcharge' },

      { code: 'TRAN_TOLL_CHARGES', name: 'Toll Charges' },
      { code: 'TRAN_HIGHWAY_TAX', name: 'Highway Tax' },
      { code: 'TRAN_STATE_ENTRY_TAX', name: 'State Entry Tax' },
      { code: 'TRAN_BRIDGE_TOLL_CHARGES', name: 'Bridge Toll Charges' },
      { code: 'TRAN_ROAD_PERMIT_CHARGES', name: 'Road Permit Charges' },
      { code: 'TRAN_GREEN_TAX', name: 'Green Tax / Environmental Tax' },

      { code: 'TRAN_ODC_PERMIT', name: 'Over Dimensional Cargo Permit' },
      { code: 'TRAN_ESCORT_VEHICLE_CHARGES', name: 'Escort Vehicle Charges' },
      { code: 'TRAN_POLICE_ESCORT_CHARGES', name: 'Police Escort Charges' },
      { code: 'TRAN_ROUTE_SURVEY_CHARGES', name: 'Route Survey Charges' },
      { code: 'TRAN_SPECIAL_MOVEMENT_PERMIT', name: 'Special Movement Permit' },

      { code: 'TRAN_REEFER_MONITORING_CHARGES', name: 'Reefer Monitoring Charges' },
      { code: 'TRAN_TEMPERATURE_CONTROL_CHARGES', name: 'Temperature Control Charges' },
      { code: 'TRAN_DRY_ICE_HANDLING_CHARGES', name: 'Dry Ice Handling Charges' },

      { code: 'TRAN_CARGO_INSURANCE_CHARGES', name: 'Cargo Insurance Charges' },
      { code: 'TRAN_TRANSPORT_LIABILITY_COVERAGE', name: 'Transport Liability Coverage' },
      { code: 'TRAN_RISK_SURCHARGE', name: 'Risk Surcharge' },
      { code: 'TRAN_HIGH_VALUE_CARGO_SURCHARGE', name: 'High Value Cargo Surcharge' },

      { code: 'TRAN_BORDER_CLEARANCE_HANDLING', name: 'Border Clearance Handling' },
      { code: 'TRAN_TRANSIT_DOCUMENTATION_CHARGES', name: 'Transit Documentation Charges' },
      { code: 'TRAN_TIR_PROCESSING_CHARGES', name: 'TIR Processing Charges' },
      { code: 'TRAN_CROSS_BORDER_PERMIT_CHARGES', name: 'Cross Border Permit Charges' },
      { code: 'TRAN_CUSTOMS_TRANSIT_CHARGES', name: 'Customs Transit Charges' },

      { code: 'TRAN_TRANSPORT_MANAGEMENT_FEE', name: 'Transport Management Fee' },
      { code: 'TRAN_TRANSPORT_DOCUMENTATION_CHARGES', name: 'Documentation Charges' },
      { code: 'TRAN_POD_HANDLING_CHARGES', name: 'POD Handling Charges' },
      { code: 'TRAN_PROOF_OF_DELIVERY_PROCESSING', name: 'Proof of Delivery Processing' },
      { code: 'TRAN_DISPATCH_COORDINATION_CHARGES', name: 'Dispatch Coordination Charges' },
      { code: 'TRAN_TRACKING_MONITORING_CHARGES', name: 'Tracking & Monitoring Charges' },
      { code: 'TRAN_GPS_TRACKING_CHARGES', name: 'GPS Tracking Charges' }
    ],
    'Destination Handling': [
      { code: 'TRAN_CONTAINER_HAULAGE_CHARGES', name: 'Container Haulage Charges' },
      { code: 'TRAN_PORT_PICKUP_CHARGES', name: 'Port Pickup Charges' },
      { code: 'TRAN_PORT_DELIVERY_CHARGES', name: 'Port Delivery Charges' },
      { code: 'TRAN_ICD_TRANSPORTATION_CHARGES', name: 'ICD Transportation Charges' },
      { code: 'TRAN_RAIL_RAMP_TRANSPORTATION', name: 'Rail Ramp Transportation' },
      { code: 'TRAN_PORT_ENTRY_FEE', name: 'Port Entry Fee' },
      { code: 'TRAN_TERMINAL_ENTRY_CHARGES', name: 'Terminal Entry Charges' },
      { code: 'TRAN_CONTAINER_LIFT_ON_CHARGES', name: 'Container Lift-On Charges' },
      { code: 'TRAN_CONTAINER_LIFT_OFF_CHARGES', name: 'Container Lift-Off Charges' },
      { code: 'TRAN_CONTAINER_GROUNDING_CHARGES', name: 'Container Grounding Charges' },
      { code: 'TRAN_EMPTY_CONTAINER_RETURN_CHARGES', name: 'Empty Container Return Charges' }
    ],
    'Delivery': [
      { code: 'TRAN_LAST_MILE_DELIVERY_CHARGES', name: 'Last Mile Delivery Charges' },
      { code: 'TRAN_MULTI_DROP_DELIVERY_CHARGES', name: 'Multi Drop Delivery Charges' },
      { code: 'TRAN_REVERSE_LOGISTICS_CHARGES', name: 'Reverse Logistics Charges' },
      { code: 'TRAN_FAILED_DELIVERY_CHARGES', name: 'Failed Delivery Charges' },
      { code: 'TRAN_DELIVERY_APPOINTMENT_CHARGES', name: 'Delivery Appointment Charges' },
      { code: 'TRAN_RESIDENTIAL_DELIVERY_CHARGES', name: 'Residential Delivery Charges' }
    ],
    'Exception / Penalty': [
      { code: 'TRAN_AFTER_HOURS_DELIVERY_CHARGES', name: 'After Hours Delivery Charges' },
      { code: 'TRAN_WEEKEND_DELIVERY_CHARGES', name: 'Weekend Delivery Charges' },
      { code: 'TRAN_HOLIDAY_SURCHARGE', name: 'Holiday Surcharge' },
      { code: 'TRAN_EMERGENCY_DELIVERY_CHARGES', name: 'Emergency Delivery Charges' },
      { code: 'TRAN_ROUTE_DIVERSION_CHARGES', name: 'Route Diversion Charges' },
      { code: 'TRAN_CARGO_DAMAGE_HANDLING_CHARGES', name: 'Cargo Damage Handling Charges' }
    ]
  };

  const WAREHOUSING = {
    'Handling': [
      // Inbound
      { code: 'WH_IN_UNLOADING', name: 'Unloading Charges (Manual / Forklift)' },
      { code: 'WH_IN_DESTUFFING', name: 'Container Destuffing Charges' },
      { code: 'WH_IN_PALLET_UNLOADING', name: 'Pallet Unloading' },
      { code: 'WH_IN_FLOOR_UNLOADING', name: 'Floor Unloading' },
      { code: 'WH_IN_DOCK_HANDLING', name: 'Dock Handling Charges' },
      { code: 'WH_IN_RECEIVING', name: 'Receiving Charges' },
      { code: 'WH_IN_GRN_PROCESSING', name: 'GRN Processing Charges' },
      { code: 'WH_IN_SKU_VERIFICATION', name: 'SKU Verification Charges' },
      { code: 'WH_IN_SORTING_SEGREGATION', name: 'Sorting / Segregation' },
      { code: 'WH_IN_SHORT_EXCESS_HANDLING', name: 'Short / Excess Handling' },
      { code: 'WH_IN_DAMAGE_INSPECTION', name: 'Damage Inspection Charges' },
      { code: 'WH_IN_PUT_AWAY', name: 'Put-Away Charges' },
      { code: 'WH_IN_BARCODE_LABELLING', name: 'Barcode Labelling (Inbound)' },
      { code: 'WH_IN_PALLET_TAGGING', name: 'Pallet Tagging Charges' },
      // Inventory/internal movement
      { code: 'WH_INV_REWAREHOUSING', name: 'Re-Warehousing Charges' },
      { code: 'WH_INV_INTERNAL_SHIFTING', name: 'Internal Shifting Charges' },
      { code: 'WH_INV_LOCATION_TRANSFER', name: 'Location Transfer Charges' },
      { code: 'WH_INV_CYCLE_COUNT', name: 'Cycle Count Charges' },
      { code: 'WH_INV_PHYSICAL_AUDIT', name: 'Physical Inventory Audit Charges' },
      { code: 'WH_INV_STOCK_RECONCILIATION', name: 'Stock Reconciliation Charges' },
      { code: 'WH_INV_WMS_TRANSACTION_FEE', name: 'System Update / WMS Transaction Fee' },
      // Outbound
      { code: 'WH_OUT_ORDER_PICKING_LINE', name: 'Order Picking Charges (Per Line Item)' },
      { code: 'WH_OUT_PICK_PALLET', name: 'Picking Per Pallet' },
      { code: 'WH_OUT_PICK_CARTON', name: 'Picking Per Carton' },
      { code: 'WH_OUT_PACKING_REPACKING', name: 'Packing / Repacking Charges' },
      { code: 'WH_OUT_KITTING_BUNDLING', name: 'Kitting / Bundling Charges' },
      { code: 'WH_OUT_PALLETISATION', name: 'Palletisation Charges' },
      { code: 'WH_OUT_STRETCH_WRAPPING', name: 'Stretch Wrapping' },
      { code: 'WH_OUT_LABEL_RELABELLING', name: 'Labelling / Relabelling' },
      { code: 'WH_OUT_EXPORT_PACKING', name: 'Export Packing' },
      { code: 'WH_OUT_DG_PACKING', name: 'Dangerous Goods Packing' },
      { code: 'WH_OUT_MARK_STENCILLING', name: 'Marking & Stencilling' },
      { code: 'WH_OUT_DISPATCH_HANDLING', name: 'Dispatch Handling Charges' },
      { code: 'WH_OUT_DOCK_LOADING', name: 'Dock Loading Charges' }
    ],
    'Storage': [
      { code: 'WH_STORE_PALLET_STORAGE', name: 'Pallet Storage Per Day / Week / Month' },
      { code: 'WH_STORE_BIN_STORAGE', name: 'Bin Storage Charges' },
      { code: 'WH_STORE_SHELF_STORAGE', name: 'Shelf Storage Charges' },
      { code: 'WH_STORE_FLOOR_SPACE', name: 'Floor Space Charges (Per Sqft / Sqm)' },
      { code: 'WH_STORE_CAPACITY_CBM', name: 'CBM Based Storage' },
      { code: 'WH_STORE_TONNAGE', name: 'Tonnage Based Storage' },
      { code: 'WH_STORE_OPEN_YARD', name: 'Open Yard Storage' },
      { code: 'WH_STORE_HAZ_SURCHARGE', name: 'Hazardous Storage Surcharge' },
      { code: 'WH_STORE_TEMP_CONTROLLED', name: 'Temperature Controlled Storage' },
      { code: 'WH_STORE_REEFER_PLUGIN', name: 'Reefer Plug-in Charges' },
      { code: 'WH_STORE_BONDED_PREMIUM', name: 'Bonded Warehouse Premium' },
      { code: 'WH_STORE_FTWZ_PREMIUM', name: 'FTWZ Storage Premium' },
      { code: 'WH_STORE_LONG_STAY', name: 'Long Stay Storage Surcharge' },
      { code: 'WH_STORE_PEAK_SEASON', name: 'Peak Season Storage Surcharge' }
    ],
    'Compliance': [
      { code: 'WH_COMP_BOND_EXECUTION', name: 'Bond Execution Charges' },
      { code: 'WH_COMP_BOND_RENEWAL', name: 'Customs Bond Renewal' },
      { code: 'WH_COMP_EX_BOND_CLEARANCE', name: 'Ex-Bond Clearance Handling' },
      { code: 'WH_COMP_IN_BOND_TRANSFER', name: 'In-Bond Transfer Handling' },
      { code: 'WH_COMP_CUSTOMS_SUPERVISION', name: 'Customs Supervision Charges' },
      { code: 'WH_COMP_SEZ_DOCUMENTATION', name: 'SEZ Documentation Charges' },
      { code: 'WH_COMP_FTWZ_TRANSACTION_FEE', name: 'FTWZ Transaction Fee' },
      { code: 'WH_COMP_DUTY_DEFERMENT', name: 'Duty Deferment Handling Charges' },
      { code: 'WH_COMP_COMPLIANCE_FILING', name: 'Compliance Filing Charges' },
      { code: 'WH_COMP_AUDIT_SUPPORT', name: 'Audit Support Charges' }
    ],
    'Transport Interface': [
      { code: 'WH_TRANS_LOADING', name: 'Loading Charges' },
      { code: 'WH_TRANS_TRUCK_PLACEMENT', name: 'Truck Placement Charges' },
      { code: 'WH_TRANS_VEHICLE_WAITING', name: 'Vehicle Waiting Charges' },
      { code: 'WH_TRANS_GATE_PASS', name: 'Gate Pass Charges' },
      { code: 'WH_TRANS_DISPATCH_DOCUMENTATION', name: 'Dispatch Documentation' },
      { code: 'WH_TRANS_EWAY_BILL_PROCESSING', name: 'E-Way Bill Processing' },
      { code: 'WH_TRANS_LAST_MILE_COORD', name: 'Last Mile Coordination Fee' }
    ],
    'Value Added': [
      { code: 'WH_SPECIAL_TEMP_MONITORING', name: 'Temperature Monitoring Charges' },
      { code: 'WH_SPECIAL_DG_HANDLING', name: 'DG Handling Charges' },
      { code: 'WH_SPECIAL_INSURANCE_ARRANGEMENT', name: 'Insurance Arrangement Fee' },
      { code: 'WH_SPECIAL_CARGO_SURVEY', name: 'Cargo Survey Charges' },
      { code: 'WH_SPECIAL_FUMIGATION_COORDINATION', name: 'Fumigation Coordination' },
      { code: 'WH_SPECIAL_PEST_CONTROL', name: 'Pest Control Charges' },
      { code: 'WH_SPECIAL_QUALITY_INSPECTION', name: 'Quality Inspection Charges' },
      { code: 'WH_SPECIAL_SAMPLING', name: 'Sampling Charges' },
      { code: 'WH_SPECIAL_CONTAINER_STUFFING_WAREHOUSE', name: 'Container Stuffing at Warehouse' },
      { code: 'WH_SPECIAL_CROSS_DOCKING', name: 'Cross Docking Charges' },
      { code: 'WH_SPECIAL_REVERSE_LOGISTICS', name: 'Reverse Logistics Handling' }
    ]
    ,
    'Warehouse Documentation & System': [
      { code: 'WH_DOC_WMS_INTEGRATION', name: 'WMS Integration Charges' },
      { code: 'WH_DOC_EDI', name: 'EDI Charges' },
      { code: 'WH_DOC_REPORTING', name: 'Reporting Charges' },
      { code: 'WH_DOC_MIS_GENERATION', name: 'MIS Generation Charges' },
      { code: 'WH_DOC_TRANSACTION_PROCESSING', name: 'Transaction Processing Fee' },
      { code: 'WH_DOC_ASN_HANDLING', name: 'ASN Handling Charges' },
      { code: 'WH_DOC_DOCUMENTATION', name: 'Documentation Charges' },
      { code: 'WH_DOC_INVOICE_PROCESSING', name: 'Invoice Processing Fee' }
    ],
    'Detention / Penalty / Exception Charges': [
      { code: 'WH_DET_CONTAINER_DET_WAREHOUSE', name: 'Container Detention at Warehouse' },
      { code: 'WH_DET_GROUND_RENT', name: 'Ground Rent' },
      { code: 'WH_DET_OVERSTAY_PENALTY', name: 'Overstay Penalty' },
      { code: 'WH_DET_DAMAGE_LIABILITY', name: 'Damage Liability Handling' },
      { code: 'WH_DET_EMERGENCY_HANDLING', name: 'Emergency Handling Charges' },
      { code: 'WH_DET_AFTER_OFFICE_HOUR', name: 'After Office Hour Operation' },
      { code: 'WH_DET_HOLIDAY_WORKING', name: 'Holiday Working Charges' },
      { code: 'WH_DET_PRIORITY_HANDLING', name: 'Priority Handling Charges' }
    ]
  };

  const BREAKBULK = {
    'Origin Inland Movement': [
      { code: 'BB_PICKUP_CHARGES', name: 'Project Cargo Pick Up Charges' },
      { code: 'BB_LOWBED_CHARGES', name: 'Lowbed Trailer Charges' },
      { code: 'BB_HYDRAULIC_AXLE_TRAILER_CHARGES', name: 'Hydraulic Axle Trailer Charges' },
      { code: 'BB_FLATBED_TRUCK_CHARGES', name: 'Flatbed Truck Charges' },
      { code: 'BB_EXTENDABLE_TRAILER_CHARGES', name: 'Extendable Trailer Charges' },
      { code: 'BB_MULTI_AXLE_TRAILER_CHARGES', name: 'Multi Axle Trailer Charges' },
      { code: 'BB_ODC_TRANSPORT_CHARGES', name: 'Over Dimensional Cargo (ODC) Transport Charges' },
      { code: 'BB_ROUTE_SURVEY_CHARGES', name: 'Route Survey Charges' },
      { code: 'BB_ESCORT_VEHICLE_CHARGES', name: 'Escort Vehicle Charges' },
      { code: 'BB_POLICE_ESCORT_CHARGES', name: 'Police Escort Charges' },
      { code: 'BB_ROAD_PERMIT_CHARGES', name: 'Road Permit Charges' },
      { code: 'BB_BRIDGE_REINFORCEMENT_CHARGES', name: 'Bridge Reinforcement Charges' },
      { code: 'BB_CRANE_MOBILISATION_AT_SITE', name: 'Crane Mobilisation at Site' },
      { code: 'BB_LOADING_SUPERVISION_CHARGES', name: 'Loading Supervision Charges' },
      { code: 'BB_HEAVY_LIFT_LOADING_CHARGES', name: 'Heavy Lift Loading Charges' },
      { code: 'BB_LASHING_SECURING_CHARGES', name: 'Lashing & Securing Charges' },
      { code: 'BB_CARGO_SKIDDING_ROLLING_CHARGES', name: 'Cargo Skidding / Rolling Charges' },
      { code: 'BB_JACKING_POSITIONING_CHARGES', name: 'Jacking / Positioning Charges' }
    ],
    'Origin Handling / Terminal': [
      { code: 'BB_TERM_RECEIVING_CHARGES', name: 'Terminal Receiving Charges' },
      { code: 'BB_LAYDOWN_AREA_CHARGES', name: 'Laydown Area Charges' },
      { code: 'BB_OPEN_YARD_STORAGE', name: 'Open Yard Storage' },
      { code: 'BB_HEAVY_LIFT_HANDLING_CHARGES', name: 'Heavy Lift Handling Charges' },
      { code: 'BB_CRANE_HIRE_MOBILE_CRAWLER_HARBOUR', name: 'Crane Hire (Mobile / Crawler / Harbour Crane)' },
      { code: 'BB_FORKLIFT_HANDLING_CHARGES', name: 'Forklift Handling Charges' },
      { code: 'BB_REACH_STACKER_HANDLING_CHARGES', name: 'Reach Stacker Handling' },
      { code: 'BB_STEVEDORING_LOAD_PORT_CHARGES', name: 'Stevedoring Charges (Load Port)' },
      { code: 'BB_SLING_CHARGES', name: 'Sling Charges' },
      { code: 'BB_SPREADer_BEAM_CHARGES', name: 'Spreader Beam Charges' },
      { code: 'BB_LIFTING_GEAR_RENTAL', name: 'Lifting Gear Rental' },
      { code: 'BB_SURVEYOR_ATTENDANCE_CHARGES', name: 'Surveyor Attendance Charges' },
      { code: 'BB_PRE_LOADING_INSPECTION', name: 'Pre-Loading Inspection' }
    ],
    'Origin Documentation & Customs': [
      { code: 'BB_EXPORT_CUSTOMS_CLEARANCE', name: 'Export Customs Clearance' },
      { code: 'BB_PROJECT_CARGO_DOCUMENTATION', name: 'Project Cargo Documentation' },
      { code: 'BB_SHIPPING_BILL_FILING', name: 'Shipping Bill Filing' },
      { code: 'BB_PORT_DOCUMENTATION_CHARGES', name: 'Port Documentation Charges' },
      { code: 'BB_MATE_RECEIPT_HANDLING', name: 'Mate Receipt Handling' },
      { code: 'BB_CERTIFICATE_HANDLING', name: 'Certificate Handling' },
      { code: 'BB_DG_DECLARATION', name: 'DG Declaration (if applicable)' },
      { code: 'BB_CHARTER_PARTY_DOCUMENTATION', name: 'Charter Party Documentation' }
    ],
    'Origin Port & Terminal Charges': [
      { code: 'BB_WHARFAGE_WM_BASIS', name: 'Wharfage (Weight / Measurement basis)' },
      { code: 'BB_BERTH_HANDLING_CHARGES', name: 'Berth Handling Charges' },
      { code: 'BB_PORT_DUES_CHARGES', name: 'Port Dues' },
      { code: 'BB_PORT_STORAGE_CHARGES', name: 'Port Storage Charges' },
      { code: 'BB_HEAVY_LIFT_SURCHARGE', name: 'Heavy Lift Surcharge' },
      { code: 'BB_NON_STANDARD_CARGO_SURCHARGE', name: 'Non-Standard Cargo Surcharge' },
      { code: 'BB_VESSEL_GEAR_USAGE_CHARGES', name: 'Vessel Gear Usage Charges' },
      { code: 'BB_SHORE_CRANE_CHARGES', name: 'Shore Crane Charges' },
      { code: 'BB_STEVEDORING_LABOUR', name: 'Stevedoring Labour' },
      { code: 'BB_TERMINAL_CONGESTION_SURCHARGE', name: 'Terminal Congestion Surcharge' }
    ],
    'Ocean Freight Components': [
      { code: 'BB_OCEAN_FREIGHT_PER_FREIGHT_TON_WM', name: 'Ocean Freight Per Freight Ton (W/M basis)' },
      { code: 'BB_LUMP_SUM_PROJECT_FREIGHT', name: 'Lump Sum Project Freight' },
      { code: 'BB_HEAVY_LIFT_SURCHARGE_OCEAN', name: 'Heavy Lift Surcharge' },
      { code: 'BB_LONG_LENGTH_SURCHARGE', name: 'Long Length Surcharge' },
      { code: 'BB_OVERWIDTH_SURCHARGE', name: 'Overwidth Surcharge' },
      { code: 'BB_OVERHEIGHT_SURCHARGE', name: 'Overheight Surcharge' },
      { code: 'BB_DECK_CARGO_SURCHARGE', name: 'Deck Cargo Surcharge' },
      { code: 'BB_UNDERDECK_STOWAGE_PREMIUM', name: 'Underdeck Stowage Premium' },
      { code: 'BB_WEATHER_RISK_SURCHARGE', name: 'Weather Risk Surcharge' },
      { code: 'BB_WAR_RISK_SURCHARGE', name: 'War Risk Surcharge' },
      { code: 'BB_PIRACY_SURCHARGE', name: 'Piracy Surcharge' },
      { code: 'BB_BAF', name: 'Bunker Adjustment Factor' },
      { code: 'BB_CANAL_TRANSIT_SURCHARGE', name: 'Canal Transit Surcharge' },
      { code: 'BB_CHARTER_HIRE_COST', name: 'Charter Hire Cost (if part vessel chartered)' },
      { code: 'BB_SPACE_RESERVATION_CHARGES', name: 'Space Reservation Charges' },
      { code: 'BB_VESSEL_DIVERSION_RISK_SURCHARGE', name: 'Vessel Diversion Risk Surcharge' },

      // RoRo specific
      { code: 'BB_RORO_FREIGHT_PER_UNIT', name: 'RoRo Freight Per Unit' },
      { code: 'BB_VEHICLE_HANDLING_CHARGES', name: 'Vehicle Handling Charges' },
      { code: 'BB_RORO_TERMINAL_HANDLING', name: 'RoRo Terminal Handling' },
      { code: 'BB_DRIVE_IN_OUT_CHARGES', name: 'Drive-in / Drive-out Charges' },
      { code: 'BB_BATTERY_DISCONNECTION_CHARGES', name: 'Battery Disconnection Charges' },
      { code: 'BB_VEHICLE_SECURING_CHARGES', name: 'Vehicle Securing Charges' }
    ],
    'Transshipment / Intermediate Port': [
      { code: 'BB_TRANSSHIPMENT_HANDLING_CHARGES', name: 'Transshipment Handling Charges' },
      { code: 'BB_RE_LASHING_CHARGES', name: 'Re-Lashing Charges' },
      { code: 'BB_INTERMEDIATE_STORAGE', name: 'Intermediate Storage' },
      { code: 'BB_PORT_ADDITIONAL_STEVEDORING', name: 'Port Additional Stevedoring' }
    ],
    'Destination Port Charges': [
      { code: 'BB_DISCH_STEVEDORING', name: 'Discharge Stevedoring' },
      { code: 'BB_HEAVY_LIFT_DISCHARGE_CHARGES', name: 'Heavy Lift Discharge Charges' },
      { code: 'BB_CRANE_HIRE_DISCHARGE_PORT', name: 'Crane Hire (Discharge Port)' },
      { code: 'BB_WHARFAGE_DEST', name: 'Wharfage' },
      { code: 'BB_PORT_DUES_DEST', name: 'Port Dues' },
      { code: 'BB_TERMINAL_HANDLING_DEST', name: 'Terminal Handling' },
      { code: 'BB_SURVEYOR_CHARGES', name: 'Surveyor Charges' },
      { code: 'BB_LAYDOWN_YARD_CHARGES', name: 'Laydown Yard Charges' },
      { code: 'BB_CARGO_UNLASHING_CHARGES', name: 'Cargo Unlashing Charges' },
      { code: 'BB_PORT_CONGESTION_SURCHARGE', name: 'Port Congestion Surcharge' }
    ],
    'Import Clearance': [
      { code: 'BB_IMPORT_CUSTOMS_CLEARANCE', name: 'Import Customs Clearance' },
      { code: 'BB_BILL_OF_ENTRY_FILING', name: 'Bill of Entry Filing' },
      { code: 'BB_PROJECT_IMPORT_HANDLING_CHARGES', name: 'Project Import Handling Charges' },
      { code: 'BB_DUTY_ASSESSMENT_HANDLING', name: 'Duty Assessment Handling' },
      { code: 'BB_LICENSE_HANDLING', name: 'License Handling' },
      { code: 'BB_CUSTOMS_EXAMINATION', name: 'Customs Examination' },
      { code: 'BB_TECHNICAL_INSPECTION_HANDLING', name: 'Technical Inspection Handling' }
    ],
    'Destination Inland Transport': [
      { code: 'BB_HEAVY_HAULAGE_CHARGES', name: 'Heavy Haulage Charges' },
      { code: 'BB_HYDRAULIC_TRAILER_TRANSPORT', name: 'Hydraulic Trailer Transport' },
      { code: 'BB_ROUTE_SURVEY_DEST', name: 'Route Survey (Destination)' },
      { code: 'BB_ESCORT_VEHICLE_DEST', name: 'Escort Vehicle Charges' },
      { code: 'BB_ROAD_PERMIT_DEST', name: 'Road Permit Charges' },
      { code: 'BB_CRANE_UNLOADING_AT_SITE', name: 'Crane Unloading at Site' },
      { code: 'BB_JACKING_SKIDDING_AT_SITE', name: 'Jacking / Skidding at Site' },
      { code: 'BB_FINAL_POSITIONING_CHARGES', name: 'Final Positioning Charges' },
      { code: 'BB_INSTALLATION_ASSISTANCE_OPTIONAL', name: 'Installation Assistance (Optional Service)' }
    ]
  };

  return {
    'ocean-fcl': FCL,
    'ocean-lcl': LCL,
    air: AIR,
    transport: TRANSPORT,
    warehousing: WAREHOUSING,
    breakbulk: BREAKBULK
  };
};

export const CHARGE_HEADS_BY_MODE_LEG = buildChargeHeadsByModeLeg();

/**
 * Modes where charge-code lists always use every leg (full union), regardless of selected Scope.
 * Add mode ids here as you roll this out (e.g. ocean-lcl, air).
 */
export const MODES_CHARGE_DROPDOWN_SHOWS_ALL_LEGS = ['ocean-fcl', 'ocean-lcl', 'air', 'warehousing', 'transport', 'breakbulk'];

/**
 * Get charge codes (for dropdown) for a given mode and selected scope labels.
 * If no scope selected, returns all charge heads for that mode.
 * For modes in MODES_CHARGE_DROPDOWN_SHOWS_ALL_LEGS, scope is always ignored (full union).
 */
export function getChargeHeadsForModeAndScopes(modeId, selectedScopeLabels = []) {
  const byLeg = CHARGE_HEADS_BY_MODE_LEG[modeId];
  if (!byLeg) return [];

  const scopeOptions = SCOPE_OPTIONS_BY_MODE[modeId] || [];
  const ignoreScopeForCharges = MODES_CHARGE_DROPDOWN_SHOWS_ALL_LEGS.includes(modeId);
  const hasSelectedScopes = !ignoreScopeForCharges && selectedScopeLabels.length > 0;

  // Backward compatible mapping:
  // Older quotes may store the previous Warehousing leg labels (e.g. "Inbound Handling Charges").
  // We map them to the new Level-2 activity labels and intentionally drop the excluded legs.
  let normalizedScopeLabels = selectedScopeLabels;
  if (modeId === 'warehousing') {
    const WAREHOUSING_SCOPE_ALIAS = {
      'Inbound Handling Charges': 'Handling',
      'Inventory Handling & Internal Movement': 'Handling',
      'Outbound Handling Charges': 'Handling',
      'Storage Charges': 'Storage',
      'Warehouse Transportation Interface': 'Transport Interface',
      'Compliance & Regulatory': 'Compliance',
      'Special Handling / Value Added Services': 'Value Added',
      'Warehouse Documentation & System': 'Warehouse Documentation & System'
    };
    normalizedScopeLabels = selectedScopeLabels
      .map((s) => WAREHOUSING_SCOPE_ALIAS[s] ?? s)
      .filter(Boolean);
  }

  if (modeId === 'breakbulk') {
    const BREAKBULK_SCOPE_ALIAS = {
      'Port & Terminal Charges': 'Origin Port & Terminal Charges'
    };
    normalizedScopeLabels = selectedScopeLabels
      .map((s) => BREAKBULK_SCOPE_ALIAS[s] ?? s)
      .filter((s) => s !== 'Risk / Compliance / Special');
  }

  const scopesToUse = hasSelectedScopes
    ? normalizedScopeLabels.filter((s) => scopeOptions.includes(s))
    : scopeOptions;

  const seen = new Set();
  const result = [];
  for (const leg of scopesToUse) {
    const heads = byLeg[leg] || [];
    for (const { code, name } of heads) {
      if (!seen.has(code)) {
        seen.add(code);
        result.push({ code, name });
      }
    }
  }
  return result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}
